import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _ from 'lodash';
import moment from 'moment';
import {
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Chip,
} from '@material-ui/core';
import { DatePicker, TimePicker } from 'material-ui-pickers';

import FormValidator from './components/FormValidator';

const STRING_TYPES = ['string', 'number', 'phone', 'email'];

class FormBuilder extends React.Component {
  static propTypes = {
    initialValues: PropTypes.object,
    config: PropTypes.object,
    renderFormActions: PropTypes.func,
    errors: PropTypes.object,
    clearErrorForField: PropTypes.func,
    formStyle: PropTypes.object,
  };

  static defaultProps = {
    initialValues: {},
    config: {},
    renderFormActions: () => {},
    errors: {},
    clearErrorForField: () => {},
    formStyle: {},
  };

  constructor(props) {
    super(props);

    this.state = this.getInitialState(props.config);
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.config, this.props.config)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(this.getInitialState(this.props.config));
    }
  }

  getFields = (config) => {
    let fields = [];
    if (config.type === 'nested-form') {
      // eslint-disable-next-line max-len
      const fieldsData = config.data && config.data.reduce((acc, formdata) => [...acc, ...formdata.data], []);
      fields = fieldsData;
    } else {
      fields = config.data || [];
    }

    return fields;
  };

  getInitialState = (config) => {
    const { initialValues } = this.props;
    const fieldsData = this.getFields(config);

    return fieldsData.reduce((acc, field) => {
      if (
        STRING_TYPES.includes(field.type)
        || field.type === 'textarea'
        || field.type === 'date'
        || field.type === 'time'
        || field.type === 'choice'
      ) {
        return { ...acc, [field.id]: initialValues[field.id] || field.defaultValue || '' };
      }
      if (field.type === 'select') {
        let value = '';
        if (initialValues[field.id] !== null && initialValues[field.id] !== undefined) {
          value = initialValues[field.id];
        } else if (field.defaultValue !== null && field.defaultValue !== undefined) {
          value = field.defaultValue;
        }

        return { ...acc, [field.id]: value };
      }
      if (field.type === 'multiselect') {
        return { ...acc, [field.id]: initialValues[field.id] || field.defaultValue || [] };
      }
      return { ...acc, [field.id]: initialValues[field.id] || field.defaultValue };
    }, {});
  };

  getFormData = () => this.state;

  getTextInputType = (fieldType) => {
    if (fieldType === 'password') {
      return 'password';
    }
    if (fieldType === 'number') {
      return 'number';
    }
    return 'text';
  };

  generateValidation = () => {
    const validations = {};
    const fieldsData = this.getFields(this.props.config);

    fieldsData.forEach((fieldData) => {
      const { id, required, type } = fieldData;
      const fieldValidation = [];

      if (required) {
        fieldValidation.push('required');
      }
      if (type === 'email' || type === 'number' || type === 'phone') {
        fieldValidation.push(type);
      }

      validations[id] = fieldValidation;
    });

    return validations;
  };

  generateField = (fieldData, validateField, errorMessages) => {
    let fieldComponent = null;
    const uniqueId = this.props.config.title || '';

    if (STRING_TYPES.includes(fieldData.type)) {
      fieldComponent = (
        <TextField
          disabled={fieldData.disabled}
          id={`${fieldData.id}-${uniqueId}`}
          placeholder={fieldData.label}
          label={fieldData.label}
          value={this.state[fieldData.id]}
          onChange={(event) => {
            const { value } = event.target;
            this.props.clearErrorForField(fieldData.id);
            validateField(fieldData.id, value);
            this.setState({
              [fieldData.id]: value,
            });
          }}
          helperText={
            (errorMessages[fieldData.id] && errorMessages[fieldData.id].join(', '))
            || this.props.errors[fieldData.id]
          }
          error={
            (errorMessages[fieldData.id] && errorMessages[fieldData.id].length > 0)
            || !!this.props.errors[fieldData.id]
          }
          type={this.getTextInputType(fieldData.type)}
          fullWidth
        />
      );
    } else if (fieldData.type === 'textarea') {
      fieldComponent = (
        <TextField
          disabled={fieldData.disabled}
          id={`${fieldData.id}-${uniqueId}`}
          placeholder={fieldData.label}
          label={fieldData.label}
          value={this.state[fieldData.id]}
          onChange={(event) => {
            const { value } = event.target;
            this.props.clearErrorForField(fieldData.id);
            validateField(fieldData.id, value);
            this.setState({
              [fieldData.id]: value,
            });
          }}
          helperText={
            (errorMessages[fieldData.id] && errorMessages[fieldData.id].join(', '))
            || this.props.errors[fieldData.id]
          }
          error={
            (errorMessages[fieldData.id] && errorMessages[fieldData.id].length > 0)
            || !!this.props.errors[fieldData.id]
          }
          type={this.getTextInputType(fieldData.type)}
          rows={2}
          rowsMax={3}
          multiline
          fullWidth
        />
      );
    } else if (fieldData.type === 'select') {
      const isError = (errorMessages[fieldData.id] && errorMessages[fieldData.id].length > 0)
        || !!this.props.errors[fieldData.id];

      fieldComponent = (
        <StyledFormControl error={isError}>
          <InputLabel htmlFor={`${fieldData.id}-${uniqueId}`}>{fieldData.label || ''}</InputLabel>
          <Select
            disabled={fieldData.disabled}
            value={this.state[fieldData.id]}
            onChange={(event) => {
              const { value } = event.target;
              this.props.clearErrorForField(fieldData.id);
              validateField(fieldData.id, value);
              this.setState({
                [fieldData.id]: value,
              });
            }}
            inputProps={{
              name: fieldData.id,
              id: `${fieldData.id}-${uniqueId}`,
              placeholder: fieldData.label,
            }}
            autoWidth
          >
            {fieldData.choices
              && fieldData.choices.map(choice => (
                <MenuItem key={choice.value} value={choice.value}>
                  {choice.label}
                </MenuItem>
              ))}
          </Select>
          {isError && (
            <FormHelperText>
              {(errorMessages[fieldData.id] && errorMessages[fieldData.id].join(', '))
                || this.props.errors[fieldData.id]}
            </FormHelperText>
          )}
        </StyledFormControl>
      );
    } else if (fieldData.type === 'multiselect') {
      const isError = (errorMessages[fieldData.id] && errorMessages[fieldData.id].length > 0)
        || !!this.props.errors[fieldData.id];

      fieldComponent = (
        <StyledFormControl error={isError}>
          <InputLabel htmlFor={`${fieldData.id}-${uniqueId}`}>{fieldData.label || ''}</InputLabel>
          <Select
            disabled={fieldData.disabled}
            value={this.state[fieldData.id]}
            onChange={(event) => {
              const { value } = event.target;
              this.props.clearErrorForField(fieldData.id);
              validateField(fieldData.id, value);
              this.setState({
                [fieldData.id]: value,
              });
            }}
            inputProps={{
              name: fieldData.id,
              id: `${fieldData.id}-${uniqueId}`,
              placeholder: fieldData.label,
            }}
            renderValue={selected => (
              <SelectedChipsContainer>
                {selected.map(value => (
                  <Chip key={value} label={value} />
                ))}
              </SelectedChipsContainer>
            )}
            autoWidth
            multiple
          >
            {fieldData.choices
              && fieldData.choices.map(choice => (
                <MenuItem key={choice.value} value={choice.value}>
                  {choice.label}
                </MenuItem>
              ))}
          </Select>
          {isError && (
            <FormHelperText>
              {(errorMessages[fieldData.id] && errorMessages[fieldData.id].join(', '))
                || this.props.errors[fieldData.id]}
            </FormHelperText>
          )}
        </StyledFormControl>
      );
    } else if (fieldData.type === 'date') {
      fieldComponent = (
        <StyledDatePicker
          disabled={fieldData.disabled}
          clearable
          label={fieldData.label}
          value={this.state[fieldData.id] ? moment(this.state[fieldData.id], 'DD-MM-YYYY') : null}
          format="DD-MM-YYYY"
          onChange={(date) => {
            this.props.clearErrorForField(fieldData.id);
            validateField(fieldData.id, date);
            this.setState({
              [fieldData.id]: moment(date).format('DD-MM-YYYY'),
            });
          }}
          helperText={
            (errorMessages[fieldData.id] && errorMessages[fieldData.id].join(', '))
            || this.props.errors[fieldData.id]
          }
          error={
            (errorMessages[fieldData.id] && errorMessages[fieldData.id].length > 0)
            || !!this.props.errors[fieldData.id]
          }
          maxDate={fieldData.maxDate}
          minDate={fieldData.minDate}
          openToYearSelection
        />
      );
    } else if (fieldData.type === 'time') {
      fieldComponent = (
        <StyledTimePicker
          disabled={fieldData.disabled}
          label={fieldData.label}
          value={this.state[fieldData.id] ? moment(this.state[fieldData.id], 'hh:mm A') : null}
          onChange={(date) => {
            this.props.clearErrorForField(fieldData.id);
            validateField(fieldData.id, date);
            this.setState({
              [fieldData.id]: moment(date).format('hh:mm A'),
            });
          }}
          helperText={
            (errorMessages[fieldData.id] && errorMessages[fieldData.id].join(', '))
            || this.props.errors[fieldData.id]
          }
          error={
            (errorMessages[fieldData.id] && errorMessages[fieldData.id].length > 0)
            || !!this.props.errors[fieldData.id]
          }
          clearable
        />
      );
    } else if (fieldData.type === 'choice') {
      const isError = (errorMessages[fieldData.id] && errorMessages[fieldData.id].length > 0)
        || !!this.props.errors[fieldData.id];

      fieldComponent = (
        <FormControl component="fieldset" error={isError}>
          <FormLabel>{fieldData.label}</FormLabel>
          {isError && (
            <FormHelperText>
              {(errorMessages[fieldData.id] && errorMessages[fieldData.id].join(', '))
                || this.props.errors[fieldData.id]}
            </FormHelperText>
          )}
          <RadioGroup
            aria-label={fieldData.id}
            name={fieldData.id}
            value={this.state[fieldData.id]}
            onChange={(event) => {
              const { value } = event.target;
              this.props.clearErrorForField(fieldData.id);
              validateField(fieldData.id, value);
              this.setState({
                [fieldData.id]: value,
              });
            }}
          >
            {fieldData.choices
              && fieldData.choices.map(choice => (
                <FormControlLabel
                  disabled={fieldData.disabled}
                  key={choice.value}
                  value={choice.value}
                  control={<Radio color="primary" />}
                  label={choice.label}
                />
              ))}
          </RadioGroup>
        </FormControl>
      );
    } else {
      console.log('new type', fieldData.type); // eslint-disable-line no-console
    }

    if (fieldData.visibleOn) {
      const { visibleOn } = fieldData;
      if (this.state[visibleOn.query] !== visibleOn.value) {
        return null;
      }
    }

    return fieldComponent;
  };

  renderForm = (config, validateField, errorMessages) => {
    if (config.type === 'nested-form') {
      return (
        <FormsWrapper>
          {config.data.map(formData => (
            <div key={formData.title}>
              <Typography variant="headline" gutterBottom>
                {formData.title || ''}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formData.description || ''}
              </Typography>

              <InputsContainer>
                {formData.data
                  // eslint-disable-next-line max-len
                  && formData.data.map(fieldData => (
                    <div key={`${fieldData.id}-${formData.title}`}>
                      {this.generateField(fieldData, validateField, errorMessages)}
                    </div>
                  ))}
              </InputsContainer>
            </div>
          ))}
        </FormsWrapper>
      );
    }
    return (
      <div>
        <Typography variant="headline" gutterBottom>
          {config.title || ''}
        </Typography>

        <InputsContainer>
          {config.data
            // eslint-disable-next-line max-len
            && config.data.map(fieldData => (
              <div key={`${fieldData.id}-${config.title}`}>
                {this.generateField(fieldData, validateField, errorMessages)}
              </div>
            ))}
        </InputsContainer>
      </div>
    );
  };

  render() {
    const { config, renderFormActions, formStyle } = this.props;

    return (
      <FormValidator
        validations={this.generateValidation()}
        render={({ validateField, validateAll, errorMessages }) => (
          <div>
            <div style={formStyle}>{this.renderForm(config, validateField, errorMessages)}</div>

            {renderFormActions({ validateForm: validateAll, formData: this.state })}
          </div>
        )}
      />
    );
  }
}

export default FormBuilder;

const InputsContainer = styled.div`
  margin-bottom: 30px;

  & > * + * {
    margin-top: 15px !important;
  }
`;

const StyledFormControl = styled(FormControl)`
  width: 100%;
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
`;

const StyledTimePicker = styled(TimePicker)`
  width: 100%;
`;

const FormsWrapper = styled.div`
  & > * + * {
    margin-top: 50px;
  }
`;

const SelectedChipsContainer = styled.div`
  & > * + * {
    margin-left: 5px;
  }
`;



// WEBPACK FOOTER //
// ./src/components/FormBuilder/FormBuilder.js